import React, { useContext, useState } from 'react';
import { Button, Form, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Turnstile from 'react-turnstile';
import { StatusContext } from '../context/Status';
import { API, getCachedStatus, showError, showInfo } from '../helpers';
import AuthLayout from './AuthLayout';

const PasswordResetForm = () => {
  const { t } = useTranslation();
  const [statusState] = useContext(StatusContext);
  const [email, setEmail] = useState('');
  const [sentEmail, setSentEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const status = statusState?.status || getCachedStatus();
  const turnstileSiteKey = status.turnstile_site_key || '';
  const turnstileEnabled = Boolean(status.turnstile_check && turnstileSiteKey);

  const handleSubmit = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || loading) return;
    if (turnstileEnabled && !turnstileToken) {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }

    setLoading(true);
    try {
      const res = await API.get('/api/reset_password', {
        params: { email: normalizedEmail, turnstile: turnstileToken },
      });
      if (!res?.data) return;
      const { success, message } = res.data;
      if (success) {
        setSentEmail(normalizedEmail);
      } else {
        showError(message);
        setTurnstileToken('');
        setTurnstileKey((current) => current + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      variant='reset'
      title={t('auth.reset.title')}
      subtitle={t('auth.reset.subtitle')}
    >
      {sentEmail ? (
        <div className='auth-result' role='status'>
          <span className='auth-result-icon'>
            <Icon name='mail outline' />
          </span>
          <strong>{t('auth.reset.sent_title')}</strong>
          <p>{t('auth.reset.sent_description', { email: sentEmail })}</p>
          <Link to='/login' className='auth-primary-link'>
            <Icon name='arrow left' />
            {t('auth.reset.back_login')}
          </Link>
        </div>
      ) : (
        <>
          <Form size='large' onSubmit={handleSubmit}>
            <Form.Input
              fluid
              icon='mail outline'
              iconPosition='left'
              placeholder={t('auth.reset.email')}
              name='email'
              type='email'
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete='email'
              autoFocus
            />
            {turnstileEnabled && (
              <div className='auth-turnstile'>
                <Turnstile
                  key={turnstileKey}
                  sitekey={turnstileSiteKey}
                  size='flexible'
                  fixedSize
                  refreshExpired='auto'
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')}
                />
              </div>
            )}
            <Button
              fluid
              type='submit'
              loading={loading}
              disabled={loading || !email.trim()}
              className='auth-primary-button'
            >
              <span>
                {loading ? t('auth.reset.sending') : t('auth.reset.button')}
              </span>
              <i className='arrow right icon' aria-hidden='true' />
            </Button>
          </Form>
          <p className='auth-form-note'>{t('auth.reset.notice')}</p>
          <div className='auth-register-prompt'>
            <Link to='/login' className='auth-text-link'>
              {t('auth.reset.back_login')}
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
};

export default PasswordResetForm;
